import { useCallback, useState } from 'react';
import { useLogin } from '../../../../hooks/useLogin';
import {
  isLoginResultSuccess,
  loginByEmail,
  LoginResult,
  loginByAuthorizationCode,
  loginByLegacyApiToken,
  loginByUUID,
  deleteTokens,
} from '../../../../api/jwtAuth';
import { ApiTokenType } from '../../../../context/AuthContext';

export const useJwtAuth = () => {
  const [loginErrors, setLoginErrors] = useState<boolean>(false);
  const login = useLogin();

  const processLoginResult = useCallback(
    (loginResult: LoginResult): 'success' | LoginResult => {
      if (isLoginResultSuccess(loginResult)) {
        login(`Bearer ${loginResult.accessToken}`, ApiTokenType.JWT);
        return 'success';
      } else {
        setLoginErrors(true);
        return loginResult;
      }
    },
    [login],
  );

  const doLogin = useCallback(
    async (email: string, password: string) => {
      const loginResult = await loginByEmail(email, password);
      processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const doLoginByAuthorizationCode = useCallback(
    async (authorizationCode: string) => {
      await deleteTokens();
      const loginResult = await loginByAuthorizationCode(authorizationCode);
      processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const doLoginByLegacyApiToken = useCallback(
    async (apiToken: string) => {
      await deleteTokens();
      const loginResult = await loginByLegacyApiToken(apiToken);
      processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  const doLoginByUUID = useCallback(
    async (
      uuid: string,
      organizationId: string,
    ): Promise<'success' | LoginResult> => {
      const loginResult = await loginByUUID(uuid, organizationId);
      return processLoginResult(loginResult);
    },
    [processLoginResult],
  );

  return {
    doLogin,
    doLoginByAuthorizationCode,
    doLoginByLegacyApiToken,
    doLoginByUUID,
    loginErrors,
  };
};
